import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { useMutation } from '@apollo/client'
import { flowRight } from 'lodash'
import { Panel } from 'rc-collapse'
import { Form } from 'formik'
import * as Nope from 'lib/utils/validation/Nope'
import * as Yup from 'yup'
import { newNotification } from 'lib/utils/store/notifications'
import NOTIFICATION_TYPE from 'lib/requestStatus'
import { isAllowedRedirectUrl, isBase64Encoded } from 'lib/helpers'
import { withErrorBoundary } from 'lib/errorBoundary'
import { logException } from 'lib/errors'
import useCurrentUser from 'lib/hooks/useCurrentUser'
import useAdPostPreventBacKNav from './useAdPostPreventBacKNav'
import { withProfiler } from '@sentry/react'
import { FINLAND_ID } from 'lib/constants'
import { getModalUrl } from 'lib/modals'
import { getLocale } from 'lib/utils/getLocale'

import useIdentificationLandingPage from 'components/User/useIdentificationLandingPage'
import AppContext from 'components/AppContext'
import Background from 'components/Backgrounds/Background/Background'
import Link from 'components/Link'
import Button from 'components/UI/Button'
import ExtendedFormik from 'components/Forms/ExtendedFormik'
import AppWindowBreakpoints from 'components/Util/WindowBreakpoints/AppWindowBreakpoints'
import ErrorComponent from 'components/Errors/ErrorComponent'
import UserEditHeader from './UserEditHeader/UserEditHeader'
import UserEditInfo from './UserEditInfo/UserEditInfo'
import StatusMessage from 'components/StatusMessage'
import UserPrimaryDataSection from './UserPrimaryDataSection'
import EditUserCollapse from './EditUserCollapse/EditUserCollapse'
import UserIdentificationDataSection from './UserIdentificationDataSection'
import UserOwnInfoDataSection from './UserOwnInfoDataSection'
import UserServiceSettingsSection from './UserServiceSettingsSection'
import UserReviewInfoSection from './UserReviewInfoSection'

import updateMeMutation from 'graphql/user/updateMe.gql'

import './user-edit.pcss'

/**
 * User should be come to this page in 4 case
 * MOBILE_VERIFICATION: For mobile number verify (vertical inbox)
 * AD_POST: Any address field missing when user try to ad post (Possible for all vertical)
 * BOARDING_VERIFICATION: Use for PGW boarding (currently only for nettiauto)
 * Default is edit_profile (Possible for all vertical)
 */
export const REDIRECT_USER_TYPE = Object.freeze({
    MOBILE_VERIFICATION: 'mobile_verification',
    AD_POST: 'ad_post',
    BOARDING_VERIFICATION: 'boarding_verification',
})

const UserEdit = () => {
    const { error: identificationError } = useIdentificationLandingPage()
    const currentUser = useCurrentUser()
    const history = useHistory()
    const location = useLocation()
    const user = currentUser.me.user
    const [fillRequiredInfoMessage, setFillRequiredInfoMessage] = useState(false)
    const [query] = useState(queryString.parse(location.search))
    const [zipCodeError, setZipCodeError] = useState(false)
    const [isFormReset, setIsFormReset] = useState(false)
    const [zipCodeLoading, setZipCodeLoading] = useState(false)
    const [phoneStatusError, setPhoneStatusError] = useState(null)
    const [errorMobileAndAdExist, setErrorMobileAndAdExist] = useState(null)
    const [errorSendVerificationCode, setErrorSendVerificationCode] = useState(null)
    const [updateMe, { loading: updateMeLoading, error: updateMeError }] = useMutation(updateMeMutation)

    const { setDisableBeforeUnload } = useAdPostPreventBacKNav(query?.type === REDIRECT_USER_TYPE.AD_POST)

    const { t, i18n } = useTranslation(['editProfile', 'formElements', 'common'])

    const [formValue, setFormValue] = useState({})

    const formInitialValues = (formValue, user, query) => {
        return {
            firstName: formValue?.firstName || user.firstName || '',
            lastName: formValue?.lastName || user.lastName || '',
            legalName: formValue?.legalName || user.legalName || '',
            mobile: formValue?.mobile || user.mobileNumber || '',
            country: formValue?.country || (user.country && user.country.id) || '',
            address: formValue?.address || user.streetAddress || '',
            zip: formValue?.zip || user.zip || '',
            city: formValue?.city || user.city || '',
            gender: formValue?.gender || user.gender || '',
            isShowPhoneNumber: formValue?.isShowPhoneNumber || !query?.isVerificationProcess ? user.isShowPhoneNumber : null,
            preferredTimeOfContact: formValue?.preferredTimeOfContact || user.preferredTimeOfContact || '',
            allowDealerFavoriteListAccess: formValue?.allowDealerFavoriteListAccess || user.allowDealerFavoriteListAccess || false,
            emailNotifyFavoriteAdPriceDrop: formValue?.emailNotifyFavoriteAdPriceDrop || user.emailNotifyFavoriteAdPriceDrop,
            allowMarketing: formValue?.allowMarketing || user.allowMarketing,
            openResultPageInNewWindow: formValue?.openResultPageInNewWindow || user.openResultPageInNewWindow,
            showPriceHistory: formValue?.showPriceHistory || user.showPriceHistory,
            language: formValue?.language || user.language,
            town: formValue?.town || user.town?.id || '',
            region: formValue?.region || user.region?.id || '',
        }
    }

    const [initialValues, setInitialValues] = useState(formInitialValues(formValue, user, query))

    let plainTextRedirectUrl = query?.redirect

    if (query?.redirect && isBase64Encoded(query.redirect)) {
        plainTextRedirectUrl = window.atob(query.redirect)
    }

    useEffect(() => {
        if (query?.isVerificationProcess) {
            history.replace(getModalUrl(history.location, 'perustiedot'))
        }

        if (user.isMobileVerified) {
            setPhoneStatusError(null)
        }
    }, [query?.isVerificationProcess, history, user.isMobileVerified])

    useEffect(() => {
        setInitialValues(formInitialValues(formValue, user, query))
    }, [formValue, user, query])

    const mobileVerificationSchema = {
        mobile: Nope.phoneNumber()
            .required('formElements:validation.required'),
        country: Nope.countryId()
            .transform((value) => Number.isNaN(value) ? null : value)
            .nullable()
            .when({
                is: (value) => !!user.country && !value,
                then: Nope.countryId()
                    .required('formElements:validation.requiredCountry'),
            })
    }

    const adPostSchema = {
        mobile: Nope.phoneNumber()
            .required('formElements:validation.required'),
        country: Nope.countryId()
            .required('formElements:validation.requiredCountry'),
        address: Nope.address()
            .required('formElements:validation.required'),
        zip: Nope.zipWithCountry(FINLAND_ID, 'country')
            .required('formElements:validation.required'),
        city: Yup.string().when('country', {
            is: (value) => parseInt(value) !== parseInt(FINLAND_ID),
            then: Nope.city()
                .required('formElements:validation.required'),
        })
    }

    const editUserSchema = {
        mobile: Nope.phoneNumber().when({
            is: (value) => !!user.mobileNumber && !value,
            then: Nope.phoneNumber()
                .required('formElements:validation.required'),
        }),
        country: Nope.countryId().when({
            is: (value) => !!user.country && !value,
            then: Nope.countryId()
                .required('formElements:validation.requiredCountry'),
        })
    }

    const defaultSchema = {
        firstName: Nope.name()
            .required('formElements:validation.required'),
        lastName: Nope.name()
            .required('formElements:validation.required'),
        legalName: query?.isVerificationProcess
            ? Yup.string()
                .required('formElements:validation.required')
            : null,
        mobile: Nope.phoneNumber(),
        country: Nope.countryId(),
        address: Nope.address(),
        zip: Nope.zipWithCountry(FINLAND_ID, 'country'),
        city: Yup.string().when('country', {
            is: (value) => parseInt(value) !== parseInt(FINLAND_ID),
            then: Nope.city()
        }),
        isShowPhoneNumber: query?.isVerificationProcess && !query?.redirectPath?.includes('aloita-kaupanteko')
            ? Nope.boolean().nullable()
                .required('formElements:validation.required')
            : null,
    }

    const locationSchema = {
        address: Yup.string().when('country', {
            is: (value) => !!value || (!!user.streetAddress && !value),
            then: Nope.address()
                .required('formElements:validation.required'),
        }),
        zip: Yup.string().when('country', {
            is: value => !!value || (!!user.zip && !value),
            then: Nope.zipWithCountry(FINLAND_ID, 'country')
                .required('formElements:validation.required'),
        }),
        city: Yup.string().when('country', {
            is: (value) => (!!value && parseInt(value) !== parseInt(FINLAND_ID)) || (!!user.city && !value),
            then: Nope.city()
                .required('formElements:validation.required'),
        })
    }

    const validationSchema = () => {
        if (query?.type === REDIRECT_USER_TYPE.MOBILE_VERIFICATION) {
            return Nope.schema({ ...defaultSchema, ...mobileVerificationSchema, ...locationSchema })
        } else if (query?.type === REDIRECT_USER_TYPE.AD_POST || query?.type === REDIRECT_USER_TYPE.BOARDING_VERIFICATION) {
            return Nope.schema({ ...defaultSchema, ...adPostSchema })
        } else {
            return Nope.schema({ ...defaultSchema, ...editUserSchema, ...locationSchema })
        }
    }

    const getInitValidation = () => {
        if (query?.type === REDIRECT_USER_TYPE.MOBILE_VERIFICATION ||
            query?.type === REDIRECT_USER_TYPE.AD_POST ||
            query?.type === REDIRECT_USER_TYPE.BOARDING_VERIFICATION) {
            return (isValid, result, { setRenderForm }) => {
                !isValid && setFillRequiredInfoMessage(true)
                setRenderForm(true)
            }
        }

        return null
    }

    const renderSectionHeader = (title, name) => {
        return (
            <div id={`section-${name}`} className="section-header">
                <h2 className="section-header__title"> {title} </h2>
            </div>
        )
    }

    const renderPrimarySection = () => {
        const header = renderSectionHeader(t('editProfile:details.primary.title'), 'primary')

        return (
            <Panel
                key="primary"
                header={header}
                forceRender
            >
                <UserPrimaryDataSection
                    zipCodeError={zipCodeError}
                    setZipCodeError={setZipCodeError}
                    isFormReset={isFormReset}
                    setIsFormReset={setIsFormReset}
                    zipCodeLoading={zipCodeLoading}
                    setZipCodeLoading={setZipCodeLoading}
                    errorMobileAndAdExist={errorMobileAndAdExist}
                    setErrorMobileAndAdExist={setErrorMobileAndAdExist}
                    errorSendVerificationCode={errorSendVerificationCode}
                    setErrorSendVerificationCode={setErrorSendVerificationCode}
                />
            </Panel>
        )
    }

    const renderIdentificationSection = () => {
        const header = renderSectionHeader(t('editProfile:details.identification.title'), 'identification')

        return (
            <Panel
                key="identification"
                header={header}
                forceRender
            >
                <UserIdentificationDataSection identificationError={identificationError} />
            </Panel>
        )
    }

    const renderOwnInfoDataSection = () => {
        const header = renderSectionHeader(t('editProfile:details.ownInfo.title'), 'ownInfo')

        return (
            <Panel
                key="ownInfo"
                header={header}
                forceRender
            >

                <UserOwnInfoDataSection />
            </Panel>
        )
    }

    const renderServiceSettingSection = () => {
        const header = renderSectionHeader(t('editProfile:details.serviceSetting.title'), 'service')

        return (
            <Panel
                key="service"
                header={header}
                forceRender
            >
                <UserServiceSettingsSection />
            </Panel>
        )
    }

    const renderReviewInfoSection = () => {
        const header = renderSectionHeader(t('editProfile:details.reviewInfo.title'), 'reviewInfo')

        return (
            <Panel
                key="reviewInfo"
                header={header}
                forceRender
            >
                <UserReviewInfoSection />
            </Panel>
        )
    }

    const renderActions = (resetForm, dirty, submitForm, values, errors, touched) => {
        return (
            <div className="context-bar">
                <div className="action-wrapper action-wrapper--right">
                    {(dirty || plainTextRedirectUrl) && (
                        <Button
                            type="button"
                            title={t('common:cancel')}
                            className="button button--secondary button--large"
                            disabled={updateMeLoading}
                            trackName="cancel"
                            onClick={(e) => {
                                if (plainTextRedirectUrl) {
                                    e.preventDefault()
                                    window.history.back()
                                } else {
                                    setIsFormReset(true)
                                    resetForm()
                                }
                            }}
                        >
                            {t('common:cancel')}
                        </Button>
                    )}
                    <Button
                        type="button"
                        trackName="save"
                        className="button button--large"
                        loading={updateMeLoading}
                        disabled={updateMeLoading || zipCodeError || zipCodeLoading}
                        onClick={() => {
                            setFormValue(values)
                            setErrorMobileAndAdExist(null)
                            setErrorSendVerificationCode(null)
                            if (query?.type === REDIRECT_USER_TYPE.AD_POST || query?.type === REDIRECT_USER_TYPE.MOBILE_VERIFICATION) {
                                const error = user.isMobileVerificationRequired
                                    ? t('editProfile:details.primary.fields.mobile.notVerifiedMessage')
                                    : null

                                setPhoneStatusError(error)
                                if (user.isMobileVerificationRequired) {
                                    return
                                }
                            }
                            if (query?.type === REDIRECT_USER_TYPE.BOARDING_VERIFICATION) {
                                const error = !user.isMobileVerified
                                    ? t('editProfile:details.primary.fields.mobile.notVerifiedMessage')
                                    : null

                                setPhoneStatusError(error)
                                if (!user.isMobileVerified) {
                                    return
                                }
                            }

                            submitForm().then(() => Object.keys(errors).length === 0 && resetForm({
                                values,
                                errors: errors,
                                touched: touched
                            }))
                        }}
                    >
                        {t('common:save')}
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <AppContext context="userEditProfile">
            <div className="user-edit">
                <AppWindowBreakpoints
                    render={({ up }) => up('containerM') ? (
                        <Background
                            imagePath="/images/OmaNettix-price-estimate-background-desktop.jpg"
                            blurHeight={600}
                            positionY="top"
                        />
                    ) : (
                        <Background
                            imagePath="/images/OmaNettix-price-estimate-background-tablet-mobile.jpg"
                            blurHeight={300}
                        />
                    )}
                />
                <div className="user-edit__container">
                    <UserEditHeader />
                    <AppContext context="userEditForm">
                        {fillRequiredInfoMessage && (
                            <div className="user-edit__warning-message">
                                <StatusMessage type="alert" icon="icon_alert_circle_yellow">{t('editProfile:fillRequiredInfo')}</StatusMessage>
                            </div>
                        )}
                        {(
                            ((query?.type === REDIRECT_USER_TYPE.AD_POST || query?.type === REDIRECT_USER_TYPE.MOBILE_VERIFICATION) &&
                                    !fillRequiredInfoMessage &&
                                    user.isMobileVerificationRequired) ||
                                (
                                    (query?.type === REDIRECT_USER_TYPE.BOARDING_VERIFICATION &&
                                        !fillRequiredInfoMessage &&
                                        !user.isMobileVerified)
                                )
                        ) && (
                            <div className="user-edit__warning-message">
                                <StatusMessage type="alert" icon="icon_alert_circle_yellow">
                                    {t('editProfile:details.primary.fields.mobile.notVerifiedMessage')}
                                </StatusMessage>
                            </div>
                        )}
                        <div className="block">
                            <ExtendedFormik
                                id="user-edit-form"
                                initialValues={initialValues}
                                enableReinitialize
                                validationSchema={validationSchema()}
                                initValidation={getInitValidation()}
                                disableInitValidationOnFormikReinitialize
                                onSubmit={async (values) => {
                                    if (phoneStatusError) {
                                        return null
                                    }

                                    const {
                                        firstName,
                                        lastName,
                                        legalName,
                                        mobile,
                                        country,
                                        address,
                                        zip,
                                        town,
                                        region,
                                        city,
                                        gender,
                                        isShowPhoneNumber,
                                        preferredTimeOfContact,
                                        allowDealerFavoriteListAccess,
                                        emailNotifyFavoriteAdPriceDrop,
                                        allowMarketing,
                                        openResultPageInNewWindow,
                                        showPriceHistory,
                                        language
                                    } = values

                                    const userInput = {
                                        firstName,
                                        lastName,
                                        legalName,
                                        allowDealerFavoriteListAccess,
                                        emailNotifyFavoriteAdPriceDrop,
                                        allowMarketing,
                                        openResultPageInNewWindow,
                                        showPriceHistory,
                                        isShowPhoneNumber,
                                    }

                                    if (gender !== '') {
                                        userInput.gender = gender
                                    } else {
                                        userInput.gender = null
                                    }
                                    if (preferredTimeOfContact !== '') {
                                        userInput.preferredTimeOfContact = preferredTimeOfContact
                                    } else {
                                        userInput.preferredTimeOfContact = null
                                    }
                                    if (mobile !== '') userInput.mobile = mobile
                                    if (address !== '') userInput.address = address
                                    if (country !== '') userInput.country = country
                                    if (zip !== '') userInput.zip = zip
                                    if (country !== FINLAND_ID && city !== '') userInput.city = city
                                    if (user.language !== language) userInput.language = language
                                    if (town !== '') userInput.town = parseInt(town)
                                    if (region !== '') userInput.region = parseInt(region)

                                    return updateMe({
                                        variables: { userInput },
                                    }).then(() => {
                                        newNotification(
                                            NOTIFICATION_TYPE.SUCCESS,
                                            '',
                                            t('editProfile:profileUpdateSuccessNotification')
                                        )
                                        setFillRequiredInfoMessage(false)
                                        setFormValue({})
                                        if (plainTextRedirectUrl) {
                                            if (isAllowedRedirectUrl(plainTextRedirectUrl)) {
                                                setDisableBeforeUnload && setDisableBeforeUnload(true)
                                                setTimeout(() => window.location.replace(plainTextRedirectUrl), 0)
                                            } else {
                                                logException('Invalid redirect url: ' + plainTextRedirectUrl)
                                            }
                                        }
                                        if (query?.isVerificationProcess === 'true' && query?.vehicleId && query?.redirectPath) {
                                            history.replace(query.redirectPath)
                                        } else if (query?.failurePath) {
                                            history.replace(query.failurePath)
                                        } else if (query?.isVerificationProcess === 'true' && query?.vehicleId) {
                                            history.replace(`${getLocale(i18n.language)}/jata-ilmoitus/${query?.vehicleId}`)
                                        }
                                    })
                                }}
                                preventDirtyNavigationMessage={
                                    query?.type === REDIRECT_USER_TYPE.AD_POST
                                        ? t('editProfile:userEditAdPostDataLostWarning.description')
                                        : t('common:formDataWillBeLostWarningMessage')
                                }
                                alwaysDisplayPreventDirtyNavigationMessage={query?.type === REDIRECT_USER_TYPE.AD_POST}
                            >
                                {({ dirty, submitCount, isValid, resetForm, submitForm, values, errors, touched }) => (
                                    <Form noValidate>
                                        <UserEditInfo />
                                        <EditUserCollapse
                                            formSubmitCount={submitCount}
                                            formIsValid={isValid}
                                            initOpenSections={identificationError ? ['identification'] : null}
                                            initOpenErrorSections={query?.isVerificationProcess && !query?.redirectPath?.includes('aloita-kaupanteko') && !formValue.isShowPhoneNumber ? ['ownInfo'] : null}
                                            errors={errors}
                                        >
                                            {renderPrimarySection()}
                                            {renderIdentificationSection()}
                                            {renderOwnInfoDataSection()}
                                            {renderServiceSettingSection()}
                                            {renderReviewInfoSection()}
                                        </EditUserCollapse>
                                        <div className="user-edit__action-bottom">
                                            <h2>{t('editProfile:details.saveChange.title')}</h2>
                                            <div>
                                                <Trans i18nKey="editProfile:details.saveChange.text">
                                                    Käyttämällä palvelua hyväksyt
                                                    <Link
                                                        to="https://www.almamedia.fi/alma-tunnus/"
                                                        className="button button--tertiary"
                                                        trackName="terms"
                                                    >
                                                        käyttöehdot
                                                    </Link>
                                                    ja
                                                    <Link
                                                        to="https://www.almamedia.fi/tietosuoja/"
                                                        className="button button--tertiary"
                                                        trackName="privacy"
                                                    >
                                                        tietosuojaselosteen
                                                    </Link>
                                                </Trans>
                                            </div>
                                            {updateMeError && <ErrorComponent error={updateMeError} />}
                                            {phoneStatusError && (
                                                <ErrorComponent
                                                    error={phoneStatusError}
                                                    contentOverride={(appError) => {
                                                        if (appError.code === 1000) {
                                                            return appError.message
                                                        }
                                                    }}
                                                />
                                            )}
                                            {renderActions(resetForm, dirty, submitForm, values, errors, touched)}
                                        </div>
                                    </Form>
                                )}
                            </ExtendedFormik>
                        </div>
                    </AppContext>
                </div>
            </div>
        </AppContext>
    )
}

export default flowRight(
    withErrorBoundary,
)(withProfiler(UserEdit, { name: 'UserEdit' }))
